import { ROUTE_DASHBOARD, ROUTE_PAYMENT } from '@/constants/routes';
import { ROUTE_JOIN_FUNNEL_BASE } from '@/app/components/join-funnel/routes';
import authService from '@/services/auth.service';
import { registerWorkosAuth } from '@/services/workos-auth-bridge';
import { useAuth } from '@workos-inc/authkit-react';
import { useCallback, useEffect } from 'react';
import { Outlet, useNavigate, useSearchParams } from 'react-router-dom';

const workosClientId = import.meta.env.VITE_WORKOS_CLIENT_ID;

/**
 * Whitelist of paths the "Use a different email" link is allowed to navigate
 * back to. Prevents open-redirect via a hostile `from` query param.
 */
const ALLOWED_RETURN_PATHS = new Set<string>([ROUTE_PAYMENT, '/sign-up']);

/**
 * Redirects to WorkOS hosted login when the feature flag is enabled.
 * Only rendered when AuthKitProvider is present (VITE_WORKOS_CLIENT_ID is set).
 *
 * Supports three query params for friendlier hand-offs:
 *   - ?email=<addr>             pre-fills the email on the AuthKit page
 *                               via loginHint, so the user only has to tap
 *                               "Continue" to get a magic link.
 *   - ?reason=already_member    shows a "you already have access" banner
 *                               instead of auto-redirecting to AuthKit
 *                               (incident 2026-05-12 — VIP-comp recipients
 *                               landing here from the payment page).
 *   - ?reason=no_account        shows a "you don't have a membership yet"
 *                               banner with a sign-up CTA. Triggered by the
 *                               response interceptor when catch_backend 401s
 *                               with code=WORKOS_USER_NOT_LINKED (incident
 *                               2026-05-13 — WorkOS users created without
 *                               externalId, no internal account exists).
 */
function WorkosSignInRedirect() {
  const { signIn, signOut, isLoading, user, getAccessToken } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const loginHintEmail = searchParams.get('email') ?? undefined;
  const reason = searchParams.get('reason');
  const isAlreadyMemberReason = reason === 'already_member';
  const isNoAccountReason = reason === 'no_account';
  // QA hook: ?debug=hold suppresses the AuthKit redirect so the interstitial
  // can be screenshotted / inspected locally. Only honored in dev builds.
  const debugHold = import.meta.env.DEV && searchParams.get('debug') === 'hold';

  // Where to send the user if they tap "Use a different email" — i.e. they
  // landed here from a checkout flow, recognized the email, but actually
  // want to buy a Catch membership for a different person. Defaults to
  // /payment (the only short-circuit caller today). Whitelisted to prevent
  // open-redirect via a hostile ?from= value.
  const rawFrom = searchParams.get('from');
  const returnToPath =
    rawFrom && ALLOWED_RETURN_PATHS.has(rawFrom) ? rawFrom : ROUTE_PAYMENT;

  // Shared: kick the user to the WorkOS hosted login with email pre-filled.
  const goToSignIn = useCallback(() => {
    signIn(loginHintEmail ? { loginHint: loginHintEmail } : undefined);
  }, [signIn, loginHintEmail]);

  // No-account: send them into the funnel to sign up properly. Hard nav so
  // any in-flight React Query / AuthKit state is dropped cleanly.
  const goToSignUp = useCallback(() => {
    window.location.href = ROUTE_JOIN_FUNNEL_BASE;
  }, []);

  // "Use a different email": send them back to the checkout origin with the
  // visitor email cleared so the form starts blank. They're buying, not
  // signing in.
  const useDifferentEmail = useCallback(() => {
    authService.clearVisitor();
    navigate(returnToPath, { replace: true });
  }, [navigate, returnToPath]);

  // No-account "try a different email": clear local state and bounce to
  // /sign-in (no `from` — they're not in a checkout flow). Hard nav so
  // AuthKit's hook re-evaluates from scratch.
  const noAccountTryDifferentEmail = useCallback(() => {
    authService.clearVisitor();
    window.location.href = '/sign-in';
  }, []);

  // Auto-redirect ONLY on the default path. Interstitial cases (VIP,
  // no-account) require deliberate user action — no surprise redirects.
  useEffect(() => {
    if (isLoading) return;
    if (debugHold) return;
    if (isAlreadyMemberReason) return;
    if (isNoAccountReason) return;

    if (user) {
      // Already authenticated via WorkOS — register the bridge and redirect
      registerWorkosAuth(getAccessToken, signOut);
      navigate(ROUTE_DASHBOARD, { replace: true });
    } else {
      goToSignIn();
    }
  }, [
    user,
    isLoading,
    isAlreadyMemberReason,
    isNoAccountReason,
    signOut,
    navigate,
    getAccessToken,
    debugHold,
    goToSignIn,
  ]);

  // VIP-comp interstitial: deliberate, user-driven hand-off. No auto-redirect.
  if (isAlreadyMemberReason) {
    return (
      <div className="dashboard-reset flex min-h-screen flex-col items-center justify-center bg-catch-tan px-6 py-12">
        <div
          data-testid="already-member-banner"
          className="flex w-full max-w-md flex-col items-center gap-5 rounded-2xl border-1 border-solid border-[#E3DCCE] bg-white p-[32px_24px] text-center text-catch-dark-blue-90 shadow-sm"
        >
          <span className="text-xs font-bold uppercase tracking-widest text-catch-dark-blue-90">
            Welcome back
          </span>
          <h1 className="m-0 text-2xl font-semibold leading-tight">
            You already have Catch&nbsp;membership
          </h1>
          <p className="m-0 text-base text-catch-dark-blue-70">
            {loginHintEmail ? (
              <>
                We recognize <span className="font-semibold text-catch-dark-blue-90">{loginHintEmail}</span>. Sign in
                to access your dashboard, risk assessment, and health data.
              </>
            ) : (
              'Sign in to access your dashboard, risk assessment, and health data.'
            )}
          </p>
          <button
            type="button"
            data-testid="already-member-continue"
            onClick={goToSignIn}
            disabled={isLoading}
            className="mt-2 inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-catch-light-blue p-[16px_24px] text-base font-semibold text-catch-dark-blue-90 transition-all hover:brightness-95 focus:outline-none focus:ring-2 focus:ring-catch-light-blue focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <span>Continue to sign in</span>
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <line x1="5" y1="12" x2="19" y2="12" />
              <polyline points="12 5 19 12 12 19" />
            </svg>
          </button>
          {loginHintEmail && (
            <button
              type="button"
              data-testid="already-member-different-email"
              onClick={useDifferentEmail}
              className="text-sm font-medium text-catch-dark-blue-70 underline-offset-2 hover:underline focus:outline-none focus:ring-2 focus:ring-catch-light-blue focus:ring-offset-2"
            >
              Use a different email to sign up
            </button>
          )}
        </div>
      </div>
    );
  }

  // No-account interstitial: WorkOS session valid but no internal members row
  // (incident 2026-05-13). Route them into the signup funnel rather than back
  // to /sign-in, which would loop them through the same broken AuthKit state.
  if (isNoAccountReason) {
    return (
      <div className="dashboard-reset flex min-h-screen flex-col items-center justify-center bg-catch-tan px-6 py-12">
        <div
          data-testid="no-account-banner"
          className="flex w-full max-w-md flex-col items-center gap-5 rounded-2xl border-1 border-solid border-[#E3DCCE] bg-white p-[32px_24px] text-center text-catch-dark-blue-90 shadow-sm"
        >
          <span className="text-xs font-bold uppercase tracking-widest text-catch-dark-blue-90">
            Welcome
          </span>
          <h1 className="m-0 text-2xl font-semibold leading-tight">
            You don&apos;t have a Catch&nbsp;membership yet
          </h1>
          <p className="m-0 text-base text-catch-dark-blue-70">
            {loginHintEmail ? (
              <>
                We couldn&apos;t find a Catch account for{' '}
                <span className="font-semibold text-catch-dark-blue-90">{loginHintEmail}</span>. Sign up to access
                your dashboard, risk assessment, and health data.
              </>
            ) : (
              'Sign up to access your dashboard, risk assessment, and health data.'
            )}
          </p>
          <button
            type="button"
            data-testid="no-account-sign-up"
            onClick={goToSignUp}
            disabled={isLoading}
            className="mt-2 inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-catch-light-blue p-[16px_24px] text-base font-semibold text-catch-dark-blue-90 transition-all hover:brightness-95 focus:outline-none focus:ring-2 focus:ring-catch-light-blue focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <span>Sign up for Catch</span>
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <line x1="5" y1="12" x2="19" y2="12" />
              <polyline points="12 5 19 12 12 19" />
            </svg>
          </button>
          <button
            type="button"
            data-testid="no-account-try-different-email"
            onClick={noAccountTryDifferentEmail}
            className="text-sm font-medium text-catch-dark-blue-70 underline-offset-2 hover:underline focus:outline-none focus:ring-2 focus:ring-catch-light-blue focus:ring-offset-2"
          >
            Already a member? Try a different email
          </button>
          <a
            data-testid="no-account-support"
            href="mailto:support@catchbio.com"
            className="text-xs text-catch-dark-blue-70 underline-offset-2 hover:underline"
          >
            Need help? Contact support
          </a>
        </div>
      </div>
    );
  }

  // Default (non-interstitial) hand-off: bare spinner while AuthKit kicks the
  // user to the hosted login page. Brief and intentional, not a state to
  // design for.
  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900" />
    </div>
  );
}

export default function SignIn() {
  if (workosClientId) {
    return <WorkosSignInRedirect />;
  }

  return <Outlet />;
}
